import { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { TenantScopeProvider, useTenantScope } from '../context/TenantScopeContext.jsx';
import TenantCompanyFilter from './TenantCompanyFilter.jsx';
import { effectiveAppRole, isWorkerRole } from '../utils/roles.js';
import AdminBackLink from './AdminBackLink.jsx';
import SupervisorEntryGate from './SupervisorEntryGate.jsx';
import PendingCheckinsBell from './PendingCheckinsBell.jsx';
import AlineaGuideAgent from './AlineaGuideAgent.jsx';
import { capabilitiesFromUser } from '../utils/solutionCapabilities.js';
import './Layout.css';

/** v2 — navegación operario solo en header (sin barra inferior). */

const SUPERADMIN_GLOBAL_PATHS = [
    '/admin/tenants',
    '/admin/onboarding',
    '/admin/ops',
    '/admin/finance',
];

/** Enlaces del header — misma fila y estilo que supervisor. */
function TopNavLinks({ role }) {
    const { user } = useAuth();
    const caps = capabilitiesFromUser(user);
    const appRole = effectiveAppRole(role);

    if (appRole === 'supervisor') {
        return (
            <>
                <NavLink to="/" className="nav-item" end>
                    <span className="nav-icon">👥</span>
                    <span className="nav-label">Tu equipo</span>
                </NavLink>
                <NavLink to="/alerts" className="nav-item">
                    <span className="nav-icon">🚨</span>
                    <span className="nav-label">Alertas</span>
                </NavLink>
                {caps.showEstadoFuncional ? (
                    <NavLink to="/analytical-boards" className="nav-item">
                        <span className="nav-icon">🛡️</span>
                        <span className="nav-label">Estado Funcional</span>
                    </NavLink>
                ) : null}
                <NavLink to="/apoyos" className="nav-item">
                    <span className="nav-icon">🧭</span>
                    <span className="nav-label">Apoyos</span>
                </NavLink>
            </>
        );
    }

    if (appRole === 'worker') {
        return (
            <>
                <NavLink to="/" className="nav-item" end>
                    <span className="nav-icon">🌤️</span>
                    <span className="nav-label">Jornada</span>
                </NavLink>
                <NavLink to="/jornada-guia" className="nav-item">
                    <span className="nav-icon">🤝</span>
                    <span className="nav-label">Guía beta</span>
                </NavLink>
                <NavLink to="/messages" className="nav-item">
                    <span className="nav-icon">💬</span>
                    <span className="nav-label">Mensajes</span>
                </NavLink>
                <NavLink to="/apoyos" className="nav-item">
                    <span className="nav-icon">✏️</span>
                    <span className="nav-label">Perfil</span>
                </NavLink>
            </>
        );
    }

    return null;
}

function AdminSidebarNav({ isSuperAdmin, onNavigate }) {
    const { platform, user } = useAuth();
    const caps = capabilitiesFromUser(user);
    const linkProps = onNavigate ? { onClick: onNavigate } : {};

    return (
        <nav id="admin-sidebar-nav" className="admin-sidebar-nav" aria-label="Panel de control">
            <p className="admin-sidebar-section">Panel de Control</p>
            <NavLink to="/" className="admin-nav-item" end {...linkProps}>
                📊 Panel Global
            </NavLink>
            <NavLink to="/indicators" className="admin-nav-item" {...linkProps}>
                📈 Indicadores Generales
            </NavLink>
            <NavLink to="/admin/diagnostico" className="admin-nav-item" {...linkProps}>
                📋 Diagnóstico y registro de manada
            </NavLink>
            <NavLink to="/admin/glosario" className="admin-nav-item" {...linkProps}>
                📖 Glosario ALINEA
            </NavLink>
            <NavLink to="/calculator" className="admin-nav-item" {...linkProps}>
                💰 Calculadora CID
            </NavLink>
            <NavLink to="/friction-map" className="admin-nav-item" {...linkProps}>
                🧩 Mapa de Fricción Sistémica
            </NavLink>
            {caps.showEstadoFuncional ? (
                <NavLink to="/analytical-boards" className="admin-nav-item" {...linkProps}>
                    🛡️ Estado Funcional
                </NavLink>
            ) : null}
            <NavLink to="/epa" className="admin-nav-item" title="Escucha · Procesamiento · Ajuste" {...linkProps}>
                🧘 EPA
            </NavLink>
            <NavLink to="/alerts" className="admin-nav-item" {...linkProps}>
                🚨 Alertas
            </NavLink>
            <NavLink to="/messages" className="admin-nav-item" {...linkProps}>
                💬 Mensajes y retroalimentación
            </NavLink>
            <NavLink to="/coach" className="admin-nav-item" {...linkProps}>
                🧭 Plan de coach
            </NavLink>
            <p className="admin-sidebar-section">Empresa y Acceso</p>
            <NavLink to="/team" className="admin-nav-item" {...linkProps}>
                👥 Base de Personal
            </NavLink>
            <NavLink to="/admin/users" className="admin-nav-item" {...linkProps}>
                🔑 Gestión de Usuarios
            </NavLink>
            <NavLink to="/admin/email-reports" className="admin-nav-item" {...linkProps}>
                📧 Reportes correo
            </NavLink>
            <NavLink to="/admin/roles" className="admin-nav-item" {...linkProps}>
                🛡️ Roles y Permisos
            </NavLink>
            {isSuperAdmin && platform?.showSuperadminNav !== false && (
                <>
                    <p className="admin-sidebar-section">Marca blanca</p>
                    <NavLink to="/admin/tenants" className="admin-nav-item" {...linkProps}>
                        🏢 Empresas (multi-tenant)
                    </NavLink>
                    <NavLink to="/admin/finance" className="admin-nav-item" {...linkProps}>
                        💵 Tablero financiero
                    </NavLink>
                    <NavLink to="/admin/onboarding" className="admin-nav-item" {...linkProps}>
                        🏗️ Implementación (plantilla maestra)
                    </NavLink>
                    <NavLink to="/admin/ops" className="admin-nav-item" {...linkProps}>
                        🛰️ Panel operacional
                    </NavLink>
                </>
            )}
        </nav>
    );
}

function AdminLayoutShell({ user, isSuperAdmin, onLogout, onGlobalPanel, children }) {
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    const closeMenu = useCallback(() => setMenuOpen(false), []);

    useEffect(() => {
        closeMenu();
    }, [location.pathname, closeMenu]);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => {
            if (e.key === 'Escape') closeMenu();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey);
        };
    }, [menuOpen, closeMenu]);

    return (
        <TenantScopeProvider>
            <div className={`layout admin-layout${menuOpen ? ' admin-layout--menu-open' : ''}`}>
                <button
                    type="button"
                    className="admin-sidebar-overlay"
                    aria-label="Cerrar menú"
                    tabIndex={menuOpen ? 0 : -1}
                    onClick={closeMenu}
                />

                <aside
                    className={`admin-sidebar${menuOpen ? ' is-open' : ''}`}
                    aria-label="Menú principal"
                    aria-hidden={!menuOpen ? undefined : false}
                >
                    <div className="admin-sidebar-top">
                        <div className="admin-sidebar-header">
                            <NavLink to="/" className="admin-sidebar-brand" onClick={closeMenu}>
                                <span className="admin-sidebar-logo" aria-hidden>
                                    ⚖️
                                </span>
                                <span className="admin-sidebar-title">ALINEA</span>
                            </NavLink>
                            <button
                                type="button"
                                className="admin-sidebar-close"
                                aria-label="Cerrar menú"
                                onClick={closeMenu}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="admin-sidebar-user">
                            <p className="admin-sidebar-user-name">{user?.nombre_completo}</p>
                            <p className="admin-sidebar-user-role text-muted text-sm">
                                {isSuperAdmin ? 'Súper Administrador' : 'Administrador de Empresa'}
                            </p>
                            {user?.tenantName && (
                                <p className="admin-sidebar-tenant text-sm">Empresa: {user.tenantName}</p>
                            )}
                            <button type="button" className="btn btn-secondary admin-sidebar-logout" onClick={onLogout}>
                                Salir
                            </button>
                        </div>
                    </div>

                    <AdminSidebarNav isSuperAdmin={isSuperAdmin} onNavigate={closeMenu} />
                </aside>

                <div className="admin-shell-main">
                    <header className="admin-top-bar">
                        <button
                            type="button"
                            className="admin-menu-toggle"
                            aria-expanded={menuOpen}
                            aria-controls="admin-sidebar-nav"
                            onClick={() => setMenuOpen((v) => !v)}
                        >
                            <span className="admin-menu-toggle-icon" aria-hidden>
                                ☰
                            </span>
                            <span className="admin-menu-toggle-label">Menú</span>
                        </button>
                        <NavLink to="/" className="admin-mobile-brand" onClick={closeMenu}>
                            <span aria-hidden>⚖️</span> ALINEA
                        </NavLink>
                        <PendingCheckinsBell />
                    </header>

                    <AdminMainContent onGlobalPanel={onGlobalPanel}>{children}</AdminMainContent>
                </div>
            </div>
        </TenantScopeProvider>
    );
}

function FieldMobileNav({ role, open, onClose, onLogout, user }) {
    if (!open) return null;

    return (
        <>
            <button type="button" className="field-nav-overlay" aria-label="Cerrar menú" onClick={onClose} />
            <div className="field-nav-drawer" role="dialog" aria-modal="true" aria-label="Navegación">
                <div className="field-nav-drawer-head">
                    <span className="field-nav-drawer-user">{(user?.nombre_completo || '').split(' ')[0]}</span>
                    <button type="button" className="field-nav-drawer-close" aria-label="Cerrar" onClick={onClose}>
                        ✕
                    </button>
                </div>
                <nav className="field-nav-drawer-links" onClick={onClose}>
                    <TopNavLinks role={role} />
                </nav>
                <button
                    type="button"
                    className="btn btn-secondary field-nav-drawer-logout"
                    onClick={() => {
                        onClose();
                        onLogout();
                    }}
                >
                    Salir
                </button>
            </div>
        </>
    );
}

function AdminMainContent({ children, onGlobalPanel }) {
    const location = useLocation();
    const { needsTenant, isSuperAdmin } = useTenantScope();
    const isGlobalSuperadminPage = SUPERADMIN_GLOBAL_PATHS.some((p) => location.pathname.startsWith(p));
    const showTenantGate = needsTenant && !isGlobalSuperadminPage;

    return (
        <main className="admin-main">
            {!onGlobalPanel && <AdminBackLink />}
            {isSuperAdmin && !isGlobalSuperadminPage && <TenantCompanyFilter />}
            <AlineaGuideAgent />
            {showTenantGate ? (
                <div className="glass-card p-md" style={{ borderLeft: '4px solid var(--color-primary)' }}>
                    <p className="text-muted" style={{ margin: 0 }}>
                        Seleccione una empresa en el filtro superior para ver los datos de ese cliente (misma vista que el
                        administrador de empresa).
                    </p>
                </div>
            ) : (
                children
            )}
        </main>
    );
}

function FieldLayoutShell({ user, userRole, useTopNav, showFieldNav, onLogout, children }) {
    const location = useLocation();
    const appRole = effectiveAppRole(userRole);
    const isSupervisor = appRole === 'supervisor';
    const [fieldMenuOpen, setFieldMenuOpen] = useState(false);
    const closeFieldMenu = useCallback(() => setFieldMenuOpen(false), []);

    useEffect(() => {
        closeFieldMenu();
    }, [location.pathname, closeFieldMenu]);

    useEffect(() => {
        if (!fieldMenuOpen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => {
            if (e.key === 'Escape') closeFieldMenu();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey);
        };
    }, [fieldMenuOpen, closeFieldMenu]);

    const shell = (
        <>
            {showFieldNav ? (
                <FieldMobileNav
                    role={userRole}
                    open={fieldMenuOpen}
                    onClose={closeFieldMenu}
                    onLogout={onLogout}
                    user={user}
                />
            ) : null}

            <header className={`layout-header${useTopNav ? ' layout-header-supervisor' : ''}`}>
                <div className="layout-header-inner">
                    {showFieldNav ? (
                        <button
                            type="button"
                            className="layout-menu-toggle"
                            aria-expanded={fieldMenuOpen}
                            aria-label="Abrir menú de navegación"
                            onClick={() => setFieldMenuOpen(true)}
                        >
                            ☰
                        </button>
                    ) : null}

                    <NavLink to="/" className="layout-brand">
                        <span className="layout-logo">⚖️</span>
                        <span className="layout-brand-text">ALINEA</span>
                    </NavLink>

                    {showFieldNav ? (
                        <nav className="layout-nav-supervisor layout-nav-desktop" aria-label="Navegación principal">
                            <TopNavLinks role={userRole} />
                        </nav>
                    ) : null}

                    <div className={`layout-user${useTopNav ? ' layout-user-supervisor' : ''}`}>
                        {isSupervisor && <PendingCheckinsBell />}
                        {user?.tenantName && (
                            <span className="layout-tenant layout-tenant-badge" title={user.tenantSlug || ''}>
                                <span className="layout-tenant-label">Empresa</span>
                                <strong>{user.tenantName}</strong>
                            </span>
                        )}
                        <span className="layout-user-name">
                            {(user?.nombre_completo || user?.name || 'Usuario').split(' ')[0]}
                        </span>
                        <button
                            type="button"
                            className={`layout-logout${useTopNav ? ' layout-logout-supervisor' : ' btn btn-secondary'}`}
                            onClick={onLogout}
                        >
                            Salir
                        </button>
                    </div>
                </div>
            </header>

            <main className={`layout-main${useTopNav ? ' layout-main-supervisor' : ''}`}>
                <AlineaGuideAgent />
                {children}
            </main>
        </>
    );

    return (
        <div className="layout">
            {isSupervisor ? <SupervisorEntryGate>{shell}</SupervisorEntryGate> : shell}
        </div>
    );
}

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    function handleLogout() {
        logout();
        navigate('/login');
    }

    const userRole = user?.rol ?? user?.role;
    const appRole = effectiveAppRole(userRole);
    const isWorker = isWorkerRole(userRole);
    const isSupervisor = appRole === 'supervisor';
    const isAdmin = userRole === 'admin' || userRole === 'superadmin';
    const isSuperAdmin = userRole === 'superadmin';
    /** Cualquier usuario de campo (no admin): header supervisor + menú arriba. */
    const useTopNav = !isAdmin;
    const showFieldNav = isSupervisor || isWorker || (!isAdmin && userRole);

    if (isAdmin) {
        const onGlobalPanel = location.pathname === '/';
        return (
            <AdminLayoutShell
                user={user}
                isSuperAdmin={isSuperAdmin}
                onLogout={handleLogout}
                onGlobalPanel={onGlobalPanel}
            >
                {children}
            </AdminLayoutShell>
        );
    }

    return (
        <FieldLayoutShell
            user={user}
            userRole={userRole}
            useTopNav={useTopNav}
            showFieldNav={showFieldNav}
            onLogout={handleLogout}
        >
            {children}
        </FieldLayoutShell>
    );
}
